import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from './supabaseClient';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // Prevents concurrent fetchProfile calls (getSession + onAuthStateChange both fire on login)
  const fetchingRef = useRef(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user, session?.provider_token);
        // On initial session restore (e.g. landing after OAuth roundtrip), also
        // honour any stashed post-login redirect. Sometimes Supabase fires
        // SIGNED_IN, sometimes only fills the session via getSession — handle
        // both. The handler clears the stash so we never navigate twice.
        try {
          const target = sessionStorage.getItem('postLoginRedirect');
          if (target) {
            sessionStorage.removeItem('postLoginRedirect');
            const tgtUrl = new URL(target);
            const here = new URL(window.location.href);
            if (tgtUrl.pathname !== here.pathname || tgtUrl.search !== here.search) {
              window.location.replace(target);
              return;
            }
            if (tgtUrl.hash && tgtUrl.hash !== here.hash) {
              window.location.hash = tgtUrl.hash;
            }
          }
        } catch (e) { console.warn('initial-session redirect failed:', e); }
      }
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user, session?.provider_token);
        // Honour a stashed post-login redirect once per SIGNED_IN event. The
        // OAuth round-trip strips hash fragments from the server-side redirect,
        // so we restore them here. We only navigate when the stash points to a
        // URL different from the current one to avoid an infinite loop.
        if (event === 'SIGNED_IN') {
          try {
            const target = sessionStorage.getItem('postLoginRedirect');
            sessionStorage.removeItem('postLoginRedirect');
            if (target) {
              const cur = window.location.href.split('?')[0]; // strip ?code= et al
              const tgt = target.split('?')[0];
              if (tgt !== cur) {
                window.location.replace(target);
                return;
              }
              // Same path but different hash — apply via assignment so React Router picks it up.
              const tgtUrl = new URL(target);
              if (tgtUrl.hash && tgtUrl.hash !== window.location.hash) {
                window.location.hash = tgtUrl.hash;
              }
            }
          } catch (e) { console.warn('post-login redirect failed:', e); }
        }
      } else { setProfile(null); setLoading(false); }
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── Fetch user info directly from Discord's API ──────────────────────────
  // Supabase does not reliably surface Discord's `global_name` or the numeric
  // user `id` (snowflake) in user_metadata. The only guaranteed source for both
  // is Discord's /users/@me endpoint, available via provider_token immediately
  // after sign-in. On subsequent session restores the token may be null, so we
  // fall back to whatever is stored in user_metadata / the DB.
  async function fetchDiscordInfo(providerToken) {
    if (!providerToken) return null;
    try {
      const res = await fetch('https://discord.com/api/users/@me', {
        headers: { Authorization: `Bearer ${providerToken}` },
      });
      if (res.ok) {
        const discordUser = await res.json();
        console.log('[AuthContext] Discord /users/@me response:', {
          id: discordUser.id,
          username: discordUser.username,
          global_name: discordUser.global_name,
        });
        return {
          id:         discordUser.id         || null,   // authoritative Discord snowflake
          globalName: discordUser.global_name || null,
          username:   discordUser.username    || null,  // unique handle (no discriminator)
        };
      }
    } catch (e) {
      console.warn('[AuthContext] Failed to fetch Discord user info:', e);
    }
    return null;
  }

  async function fetchProfile(authUser, providerToken) {
    // Guard: only one fetchProfile at a time — both getSession() and onAuthStateChange
    // fire on every login and would otherwise race against each other.
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    try {
      const userId = authUser.id;  // Supabase Auth UUID
      const meta = authUser.user_metadata || {};
      const identityData = authUser.identities?.[0]?.identity_data || {};

      // Call Discord's API first — it's the most authoritative source for both
      // the snowflake ID and the display name.
      const discordInfo = await fetchDiscordInfo(providerToken);

      // Discord's permanent numeric ID — prefer the live API response, then fall
      // back to what Supabase stored in user_metadata / identity_data.
      const discordId = discordInfo?.id
        || meta.provider_id
        || meta.sub
        || identityData.provider_id
        || identityData.sub
        || null;

      const globalName = discordInfo?.globalName || meta.global_name || identityData.global_name || null;
      const displayName = globalName || meta.full_name || meta.name || meta.preferred_username || meta.user_name || 'Unknown';
      // Prefer the live API username (most authoritative handle), then fall back
      const handle = discordInfo?.username || meta.preferred_username || meta.user_name || identityData.preferred_username || null;
      const avatarUrl = meta.avatar_url || identityData.avatar_url || null;

      console.log('[AuthContext] fetchProfile — extracted identity:', {
        userId,
        discordId,
        handle,
        globalName,
        hasProviderToken: !!providerToken,
        'meta.provider_id':          meta.provider_id,
        'meta.sub':                  meta.sub,
        'identityData.provider_id':  identityData.provider_id,
        'identityData.sub':          identityData.sub,
        'discordInfo.id':            discordInfo?.id,
      });

      // ── Step 1: Find or create the auth-linked profile ──────────────────────
      // We intentionally let the profile be created here (with the correct auth
      // UUID) before attempting to merge any older imported profile. This avoids
      // all the INSERT/UPSERT race conditions of the previous approach.
      let currentProfile = null;

      console.log('[AuthContext] Step 1: looking up existing profile by auth UUID…', userId);
      const { data: existing } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      console.log('[AuthContext] Step 1 result:', existing ? 'found existing profile' : 'no profile found', { id: existing?.id, discord_id: existing?.discord_id });

      if (existing) {
        // Sync latest Discord info on each login
        const updates = {};
        if (discordId && existing.discord_id !== discordId) updates.discord_id = discordId;
        if (avatarUrl && existing.discord_avatar !== avatarUrl) updates.discord_avatar = avatarUrl;
        // Update discord_username ONLY if we got a confirmed display name from Discord.
        if (globalName && existing.discord_username !== globalName) updates.discord_username = globalName;
        if (handle && existing.discord_handle !== handle) updates.discord_handle = handle;
        const rawRoles = meta.roles || meta.discord_roles || [];
        if (rawRoles.length > 0) updates.discord_roles = rawRoles;

        if (Object.keys(updates).length > 0) {
          await supabase.from('profiles').update(updates).eq('id', userId);
          currentProfile = { ...existing, ...updates };
        } else {
          currentProfile = existing;
        }
      } else {
        // No profile yet — truly new user (or first login after auth was created).
        // Create a pending profile with the correct auth UUID.
        console.warn('[AuthContext] Step 1: no existing profile — creating new pending profile. discordId=', discordId, 'handle=', handle, 'userId=', userId);
        const newProfile = {
          id: userId,
          discord_username: displayName,
          discord_avatar: avatarUrl,
          discord_id: discordId,
          discord_handle: handle,
          org_role: 'Member',
          member_status: 'pending',
          team: null,
          team_role: null,
          bio: '',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          availability: {},
          ships: [],
          is_admin: false,
          joined_at: new Date().toISOString(),
        };
        const { data, error } = await supabase
          .from('profiles')
          .insert([newProfile])
          .select()
          .single();
        if (error) {
          console.error('[AuthContext] Create profile error:', error.message);
          setLoading(false);
          return;
        }
        currentProfile = data;
      }

      // ── Step 2: Merge any older imported profile (SECURITY DEFINER RPC) ─────
      //
      // WHY THIS IS DONE AFTER PROFILE CREATION — NOT BEFORE:
      //   The RPC now UPDATEs the new auth-linked profile (which always exists
      //   at this point) with data from any old imported profile, then re-points
      //   foreign keys and deletes the old orphan. This avoids the INSERT/UPSERT
      //   race conditions of the previous approach.
      //
      //   Called on EVERY login (not just first), so any duplicate that survived
      //   a previous failed attempt is cleaned up automatically.
      //
      //   SECURITY DEFINER is required because RLS prevents the authenticated
      //   client from seeing profile rows whose id != auth.uid(). The RPC runs
      //   as the DB owner, finds the old imported row, and merges it atomically.
      if (discordId || handle) {
        console.log('[AuthContext] Step 2: calling link_discord_profile RPC to merge any old imported profile…', {
          p_auth_user_id: userId,
          p_discord_id:   discordId,
          p_handle:       handle,
        });
        const { data: migratedRows, error: rpcErr } = await supabase
          .rpc('link_discord_profile', {
            p_auth_user_id: userId,
            p_discord_id:   discordId,
            p_display_name: globalName || displayName,
            p_handle:       handle,
            p_avatar_url:   avatarUrl,
          });

        console.log('[AuthContext] link_discord_profile RPC result:', {
          migratedRows,
          rpcErr: rpcErr?.message,
          rowCount: migratedRows?.length ?? 0,
        });

        if (rpcErr) {
          // RPC not deployed yet, or unexpected error — log and continue with
          // the profile already fetched/created above.
          console.warn('[AuthContext] link_discord_profile RPC error (run sql/link_discord_profiles.sql):', rpcErr.message);
        } else if (migratedRows?.length > 0) {
          // Old imported profile found and merged — use the enriched profile.
          console.log('[AuthContext] Old imported profile merged into auth profile:', migratedRows[0]);
          currentProfile = migratedRows[0];
        } else {
          console.log('[AuthContext] RPC returned empty — no old imported profile found; using profile from Step 1.');
        }
      }

      setProfile(currentProfile);
      setLoading(false);

    } finally {
      fetchingRef.current = false;
    }
  }

  async function signInWithDiscord() {
    // Determine the URL the user should land on after Discord OAuth.
    //   1. ?redirect= URL-encoded param set by ProtectedRoute/MemberRoute when
    //      a logged-out user hits a gated page.
    //   2. Fall back to wherever they currently are.
    const params = new URLSearchParams(window.location.search);
    const redirectPath = params.get('redirect');
    const intendedUrl = redirectPath
      ? `${window.location.origin}${decodeURIComponent(redirectPath)}`
      : window.location.href;
    // Stash the FULL intended URL (including hash) in sessionStorage. The
    // OAuth round-trip both strips hashes AND validates redirectTo against
    // Supabase's Site URL allowlist — paths like /admin#merits typically
    // aren't on that list and silently fall back to the Site URL (home page).
    // So we always redirect to the origin root, which IS allowed, and let
    // onAuthStateChange's SIGNED_IN handler navigate to the real destination
    // after auth completes.
    try { sessionStorage.setItem('postLoginRedirect', intendedUrl); } catch {}

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'discord',
      options: {
        // Use the bare origin so Supabase's Site URL allowlist always passes.
        redirectTo: `${window.location.origin}/`,
        scopes: 'identify email guilds',
      },
    });
    if (error) console.error('Sign in error:', error);
  }

  async function signOut() {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  }

  async function updateProfile(updates) {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();
    if (!error) setProfile(data);
    return { data, error };
  }

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      loading,
      signInWithDiscord,
      signOut,
      updateProfile,
      isAdmin: profile?.is_admin ?? false,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
